import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useEntitlements } from '@/hooks/useEntitlements';

/**
 * One quiet line across the top of the app: how much of the trial is left.
 *
 * Until now the only place that said so was Instellingen → Abonnement, which
 * is the one screen a customer in his first week has no reason to open. He
 * found out his trial had ended when the app stopped working.
 *
 * Deliberately not a banner: no colour fill, no icon, no exclamation mark, one
 * line of text and a link. It is information, not an interruption — and a
 * fourteen-day countdown that shouts on day one has nothing left to say on day
 * thirteen. The only thing that changes near the end is that the text turns
 * from muted to the warning tone on the last three days.
 *
 * `trial_ends_at` is null for a paying customer (billing/service.py clears it
 * the moment they buy), so "disappear once paid" needs no second condition.
 */
export function TrialBar() {
  const { t } = useTranslation();

  // Same query key as the subscription screen (and the sidebar's lock
  // check), so this is the cached answer rather than a second request on
  // every page.
  const { data } = useEntitlements();

  const days = data?.trial_ends_at ? daysLeft(data.trial_ends_at) : null;
  if (days === null) return null;

  return (
    <div
      role="status"
      className="flex flex-wrap items-baseline justify-between gap-x-md gap-y-xs
                 border-b border-border-subtle bg-bg-secondary px-lg py-xs text-caption"
    >
      <span className={days <= 3 ? 'text-warning' : 'text-text-secondary'}>
        {days > 0
          ? t('trial.daysLeft', { count: days })
          : t('trial.lastDay')}
      </span>
      <Link to="/settings?s=subscription" className="font-medium text-accent">
        {t('trial.finish')}
      </Link>
    </div>
  );
}

/**
 * Whole days left, where the final part-day counts as zero rather than one.
 *
 * A trial ending in four hours reads "loopt vandaag af", not "nog 1 dag" —
 * saying "one day" on the morning it expires is the sentence that makes a
 * customer plan to deal with it tomorrow.
 */
function daysLeft(endsAt: string): number | null {
  const end = new Date(endsAt).getTime();
  if (Number.isNaN(end)) return null;
  const ms = end - Date.now();
  // Already over: the paywall is the message at that point, not a countdown.
  if (ms <= 0) return null;
  return Math.floor(ms / 86_400_000);
}
